import { useEffect, useState } from 'react';
import { useSuppressibleNotices } from '@/contexts/SuppressibleNoticesContext';
import { useDeviceInfo } from '@/hooks/useDeviceInfo';

export interface HomeInstallNudge {
  /** Whether to render the nudge and its detail zone at all. */
  show: boolean;
  /** Checkbox state for "Got it!" (suppress the nudge from here on). */
  dismissed: boolean;
  onDismissedChange: (checked: boolean) => void;
}

/**
 * Whether a welcome sheet should advertise adding Snowball to the Home Screen,
 * and the state of the "Got it!" checkbox that suppresses it.
 *
 * The suppression state is snapshotted when the sheet opens (or once the async
 * load completes) so the layout doesn't change reactively while the user is
 * looking at the checkbox.
 *
 * The nudge is never advertised on iOS: an installed iOS PWA has a storage
 * compartment separate from Safari, so it loses the verifications / address
 * book built up in Safari (see issue #475).
 */
export function useHomeInstallNudge(isOpen: boolean): HomeInstallNudge {
  const {
    isSuppressed,
    suppress,
    unsuppress,
    loaded: interstitialsLoaded,
  } = useSuppressibleNotices();
  const { isIOS } = useDeviceInfo();

  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (isOpen && interstitialsLoaded) {
      const suppressed = isSuppressed('homeInstall');
      setShow(!suppressed && !isIOS);
      setDismissed(suppressed);
    }
  }, [isOpen, interstitialsLoaded, isSuppressed, isIOS]);

  const onDismissedChange = (checked: boolean) => {
    setDismissed(checked);
    if (checked) suppress('homeInstall');
    else unsuppress('homeInstall');
  };

  return { show, dismissed, onDismissedChange };
}
